/**
 * Calendar-only dates (YYYY-MM-DD) must not use Date.parse / new Date(ymd),
 * which treat the value as UTC midnight and shift the local calendar day.
 */

const CALENDAR_YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Today's date in the user's local timezone as YYYY-MM-DD (for orderDate fields, inputs, Firestore filters). */
export function getLocalCalendarYMD(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse a strict YYYY-MM-DD string as local midnight. Returns null if invalid. */
export function parseCalendarYMDToLocalDate(ymd: string): Date | null {
  const t = ymd?.trim();
  const m = CALENDAR_YMD.exec(t);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, mo - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d) {
    return null;
  }
  return date;
}

/** Sort / compare key: local start-of-day for calendar strings, else parsed instant. */
export function orderDateToLocalSortable(value: string | undefined | null): number {
  if (!value) return 0;
  const trimmed = value.trim();
  const local = parseCalendarYMDToLocalDate(trimmed);
  if (local) return local.getTime();
  const t = new Date(trimmed).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Format order / delivery calendar fields for UI or CSV.
 * YYYY-MM-DD is interpreted in the local timezone.
 */
export function formatOrderDateForDisplay(
  value: Date | string | null | undefined,
  locales: string | Intl.LocalesArgument = 'en-US',
  options?: Intl.DateTimeFormatOptions
): string {
  if (value === null || value === undefined || value === '') return 'N/A';
  const defaults: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  const opts = { ...defaults, ...options };
  if (typeof value === 'string') {
    const local = parseCalendarYMDToLocalDate(value.trim());
    if (local) {
      return local.toLocaleDateString(locales, opts);
    }
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? 'N/A' : fallback.toLocaleDateString(locales, opts);
  }
  return value.toLocaleDateString(locales, opts);
}
